// =============================================================================
// FlowDefinition: represents a complete flow and, when nested, acts as a step.
// It exposes input, output, and a canExecute function.
// =============================================================================

import { uuid } from "../utils/Uuid";
import { StepDefinition } from "./StepDefinition";
import { Transition } from "./Transition";

type LocalGetCtx = <T>(key: string) => T | undefined;

export class FlowDefinition {
  public id: string; // Unique identifier for the flow
  public name: string; // Flow name
  public canExecuteFn?: LocalGetCtx; // Determines if the flow can execute
  startStep?: string; // The initial step of the flow

  // Registered simple steps keyed by their stepName.
  private steps: Map<string, StepDefinition> = new Map();

  // Links between steps: source stepName -> array of destination step names.
  private links: Map<string, string[]> = new Map();

  // Custom transitions, stored in a map where keys are step names.
  private transitions: Map<string, Transition> = new Map(); 

  // Nested flows registered in this flow.
  private nestedFlows: Map<string, FlowDefinition> = new Map();

  constructor(name: string, canExecuteFn?: LocalGetCtx) {
    this.id = uuid(); // Generate a unique ID for the flow
    this.name = name; // Set the name of the flow
    this.canExecuteFn = canExecuteFn; // Assign the execution condition function
  }

  // Sets the starting step of the flow
  setStartStep(stepName: string): void {
    if (!this.steps.has(stepName) && !this.nestedFlows.has(stepName)) {
      throw new Error(`Invalid start step: ${stepName}`); // Ensure the step exists before setting
    }
    this.startStep = stepName;
  }

  // Adds a step definition to the flow
  addStep(stepDefinition: StepDefinition): void {
    this.steps.set(stepDefinition.stepName, stepDefinition);
  }

  // Creates a link between two steps in the flow
  addLink(sourceStepName: string, destinationStepName: string): void {
    if (!this.links.has(sourceStepName)) {
      this.links.set(sourceStepName, []);
    }
    this.links.get(sourceStepName)!.push(destinationStepName);
  }

  // Adds a reconciled link, checking if the target step requires reconciliation
  addReconciledLink(source: string, target: string) {
    this.addLink(source, target);
    const targetStep = this.steps.get(target);
    if (targetStep) {
      console.log(`Enabling reconciliation for ${target}`);
    }
  }

  // Adds a transition between steps with proper type safety
  addTransition<T extends Transition>(
    TransitionClass: new (entryStep: string, exitSteps: string[]) => T,
    sourceStepName: string
  ): void {
    const exitSteps = this.links.get(sourceStepName) ?? []; // Get the exit steps
    if (exitSteps.length === 0) throw new Error(`No links for ${sourceStepName}`);

    const transition = new TransitionClass(sourceStepName, exitSteps);
    this.transitions.set(sourceStepName, transition);
  }

  // Returns the registered steps
  getSteps(): Map<string, StepDefinition> {
    return this.steps;
  }

  // Returns the registered links between steps
  getLinks(): Map<string, string[]> {
    return this.links;
  }

  // Returns the stored transitions
  getTransitions() {
    return this.transitions;
  }

  // Returns the nested flows
  getNestedFlows(): Map<string, FlowDefinition> {
    return this.nestedFlows;
  }
}